import { LogOut, UserCog } from 'lucide-react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
} from '@/components/cargofl-ui';
import { Link } from 'react-router-dom';

import { ThemeToggle } from '@/components/shell/theme-toggle';
import { TimerMenu } from '@/components/timesheet/timer-menu';
import { useSignOut, type Session } from '@/lib/session';

export function TopBar({ session }: { session: Session }) {
  const signOut = useSignOut();

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
      <SidebarTrigger />
      <div className="flex-1" />
      {/* The clock, reachable from every page rather than from one of them. */}
      <TimerMenu />
      <ThemeToggle />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button hint={"Your profile and sign out"} variant="ghost" className="h-9 gap-2 px-2">
            <Avatar className="size-7">
              {session.avatarUrl ? (
                <AvatarImage src={session.avatarUrl} alt="" />
              ) : null}
              <AvatarFallback>{initials(session.displayName)}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium sm:inline">
              {session.alias ?? session.displayName}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-medium">{session.displayName}</div>
            <div className="truncate text-xs text-muted-foreground">{session.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/profile">
              <UserCog className="size-4" />
              My profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="size-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

/** At most two letters, so the fallback never overflows the avatar. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
